/**
 * Gardeners API service
 * Handles communication with the backend for gardener management and availability
 */

import { apiGet, apiPost } from './api';

export interface GardenerResponse {
	id: number;
	name?: string;
	email: string;
	company_name?: string;
	role: 'gardener' | 'company';
	display_name: string;
	skills?: string[];
}

export interface AvailabilityResult {
	available: boolean;
	conflicts?: Array<{
		project_id: number;
		project_name: string;
		conflict_start: string;
		conflict_end: string;
		project_status: string;
	}>;
	pto_conflicts?: Array<{ start_date: string; end_date: string; label?: string | null }>;
	company_closed_dates?: Array<{ date: string; label?: string | null }>;
	gardener_id: number;
	error?: string;
}

export interface CheckAvailabilityPayload {
	gardener_ids: number[];
	start_date: string;
	end_date: string;
	exclude_project_id?: number;
}

export interface CheckAvailabilityResponse {
	all_available: boolean;
	results: AvailabilityResult[];
}

/**
 * Get all gardeners for a company
 */
export async function getCompanyGardeners(companyId: number): Promise<GardenerResponse[]> {
	return apiGet<GardenerResponse[]>(`/companies/${companyId}/gardeners`);
}

/**
 * Check availability for a single gardener
 */
export async function getGardenerAvailability(
	gardenerId: number,
	startDate: string,
	endDate: string,
	excludeProjectId?: number
): Promise<AvailabilityResult> {
	const params = new URLSearchParams({
		start_date: startDate,
		end_date: endDate
	});
	if (excludeProjectId) {
		params.append('exclude_project_id', String(excludeProjectId));
	}
	return apiGet<AvailabilityResult>(`/gardeners/${gardenerId}/availability?${params.toString()}`);
}

/**
 * Check availability for multiple gardeners
 */
export async function checkMultipleGardenersAvailability(
	payload: CheckAvailabilityPayload
): Promise<CheckAvailabilityResponse> {
	return apiPost<CheckAvailabilityResponse>('/gardeners/check-availability', payload);
}

/** Bulk time-off entries per user (for offer wizard PTO-aware timeline). */
export type BulkTimeOffResponse = Record<string, Array<{ start_date: string; end_date: string; label?: string | null }>>;

/**
 * Get time-off entries for multiple gardeners (same company only). Used when building offer timeline.
 */
export async function getBulkTimeOff(userId: number, gardenerIds: number[]): Promise<BulkTimeOffResponse> {
	if (gardenerIds.length === 0) return {};
	return apiPost<BulkTimeOffResponse>('/gardeners/time-off-bulk', { userId, user_ids: gardenerIds });
}

